<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <View gap="xl" direction="column">
        <View gap="md" direction="column" align="center">
          <Text variant="h2">Connect Google Calendar?</Text>
          <Text variant="body-large" color="secondary" align="center">
            You'll be redirected to Google to authorize Tallo to access your calendar
          </Text>
        </View>

        <WhatToExpectCard :items="whatToExpectNextItems" />
      </View>
    </template>

    <template #footer>
      <View gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" @click="connect">Continue</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { googleCalendarService } from '@tallo/auth';
import { Button, Modal, Text, View, WhatToExpectCard, WhatToExpectItem, useAlertsStore } from '@tallo/design-system';

defineEmits(['connected']);
const modalRef = ref<InstanceType<typeof Modal>>();
const alertsStore = useAlertsStore();

const whatToExpectNextItems: WhatToExpectItem[] = [
  {
    icon: 'calendar',
    title: 'Add showings to your Google Calendar',
    description: 'Tallo will automatically add your scheduled showings to your Google Calendar.',
  },
  {
    icon: 'refresh',
    title: 'Keep your calendar up to date',
    description: 'When showings are rescheduled or canceled, your calendar will be updated automatically.',
  },
  {
    icon: 'sparkle',
    title: 'See your calendar availability',
    description: 'Tallo can suggest optimal showing times based on your existing calendar events.',
  },
];

async function connect() {
  try {
    const authUrl = await googleCalendarService.connect();

    const url = new URL(authUrl);
    url.searchParams.append('isPrimary', 'true');

    close();

    window.open(url.toString(), '_blank');
  } catch (error) {
    console.error('Failed to initiate Google Calendar connection:', error);
    alertsStore.error('Failed to initiate Google Calendar connection');
  }
}

function close() {
  modalRef.value?.close();
}

defineExpose({
  modalRef,
});
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
